import Analytics from '@opencare/analytics';
import _ from 'lodash';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  MarketingButton,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import {
  createPatient,
  updateCheckoutErrorStatus,
  updatePatientDetails,
} from 'connect/actions';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import {
  selectDataCreatingPatient,
  selectDataLoadingUser,
  selectDataPatient,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { Checkbox, Form } from 'opencare-crown';

import { PartnerKey } from 'pages/partner/constants';

import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import {
  checkEmail,
  checkNumberString,
} from '../../../../common/helpers/form-field-helpers';
import { usePartnerStorage } from '../../../../partner/storage/Storage';
import { CHECKOUT_KEY } from '../../../../partner/utils';
import { selectDataCheckoutFormErrorStatus } from '../../../connect/selectors';

const analytics = new Analytics();

// Component
const PatientForm = ({ handleNextStep, state }) => {
  const dispatch = useDispatch();
  const storage = usePartnerStorage();
  const [patientForm] = Form.useForm();

  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);
  const sendingRequest = useSelector(selectDataCreatingPatient);
  const loadingUser = useSelector(selectDataLoadingUser);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );

  const [confirmDisabled, setConfirmDisabled] = useState(
    !(state && state.email && state.mobilePhone),
  );
  const [confirmChecked, setConfirmChecked] = useState(!confirmDisabled);

  const [formData, setFormData] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [stepComplete, setStepComplete] = useState(false);

  const partnerInternalIdIsVisible = user.partner.key === PartnerKey.HEALTHJOY;

  useEffect(() => {
    if (checkoutFormErrorStatus) {
      setStepComplete(false);
      setFormSubmitted(false);
    }
  }, [checkoutFormErrorStatus]);

  useEffect(() => {
    if (
      !sendingRequest
      && !loadingUser
      && patient
      && checkoutFormErrorStatus === null
      && formSubmitted
    ) {
      storage.save(CHECKOUT_KEY.PATIENT, patient, [CHECKOUT_KEY.INSURANCE]);
      setStepComplete(true);
    }
  }, [sendingRequest, loadingUser]);

  // To move to the next step one of the following must be true:
  //   1. must be a new user who filled out the form
  //   2. or a user who is already signed in who clicks continue
  useEffect(() => {
    if (stepComplete) {
      handleNextStep(formData);
    }
  }, [stepComplete]);

  const handleFormChange = () => {
    const errors = [];
    const fields = patientForm.getFieldsValue();

    const callback = (error) => {
      if (error) {
        errors.push(error);
      }
    };

    checkEmail(null, fields.email, callback);
    checkNumberString(null, fields.mobilePhone, callback);

    setConfirmDisabled(errors.length !== 0);
    dispatch(updateCheckoutErrorStatus(null));
  };

  const handleConfirmChange = (e) => {
    setConfirmChecked(e.target.checked);
  };

  const handleFormSubmit = (values) => {
    if (checkoutFormErrorStatus !== null) {
      return;
    }

    const patientDetails = {
      mobilePhone: `+1${values.mobilePhone}`,
      email: values.email,
      ...values.partnerInternalId && {
        metadata: {
          partnerInternalId: values.partnerInternalId,
        },
      },
    };

    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: 'partner_form-booking-account',
      name: 'form-booking',
      section: 'create-patient',
      content: {
        email: values.email,
        phone: values.mobilePhone.toString(),
      },
      practiceId: _.get(requestedAppointment, 'provider.clinic'),
      providerId: _.get(requestedAppointment, 'providerId'),
      userId: user.id,
      partnerId: user.partner.id,
      patientId: state.id,
    });

    setFormData(values);
    setFormSubmitted(true);

    // If email matches stored patient's email...
    if (state && state.email === patientDetails.email) {
      // If the phone also match, move forward, otherwise update patient phone number
      if (
        state.mobilePhone === values.mobilePhone
        && state.partnerInternalId === values.partnerInternalId
      ) {
        setStepComplete(true);
      } else {
        dispatch(
          updatePatientDetails({
            id: state.id,
            partnerId: user.partner.id,
            ...patientDetails,
            phoneVerified: true,
          }),
        );
      }
    } else {
      // Otherwise send request to create patient
      dispatch(createPatient(user.partner.id, patientDetails));
    }
  };

  return (
    <Form
      form={patientForm}
      className="patient-form"
      layout="vertical"
      name="basic"
      onFinish={handleFormSubmit}
      data-testid="form"
      onChange={handleFormChange}
    >
      <>
        <FloatingInput
          field="email"
          type="email"
          form={patientForm}
          label="Email"
          required
          capitalize={false}
          error={checkoutFormErrorStatus
            === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED}
          testId="email"
          className="sensitive-data"
          initialValue={_.get(state, 'email')}
          validator={checkEmail}
        />
        <FloatingInput
          field="mobilePhone"
          htmlType="tel"
          form={patientForm}
          label="Phone (Numbers Only)"
          required
          validator={checkNumberString}
          capitalize={false}
          testId="phoneNumber"
          className="sensitive-data"
          initialValue={_.get(state, 'mobilePhone')}
        />
        {partnerInternalIdIsVisible
          && (
            <FloatingInput
              field="partnerInternalId"
              form={patientForm}
              label={`${user.partner.name} Internal ID (Optional)`}
              testId="partnerInternalId"
              className="sensitive-data"
              initialValue={state?.partnerInternalId}
            />
          )}
        <Checkbox
          id="confirmed"
          name="confirmed"
          disabled={confirmDisabled}
          onChange={handleConfirmChange}
          checked={confirmChecked}
        />
        <label htmlFor="confirmed">
          {' '}
          I have confirmed that this is the correct email and phone number of
          the patient.
        </label>
      </>
      <StyledFormItem margintop="16px">
        <MarketingButton
          type="primary"
          shape="round"
          size="large"
          block
          className="patient-form-button"
          data-testid="patientFormButton"
          htmlType="submit"
          loading={formSubmitted}
          disabled={!confirmChecked}
        >
          Next
        </MarketingButton>
      </StyledFormItem>
    </Form>
  );
};

PatientForm.propTypes = {
  handleNextStep: PropTypes.func,
  state: PropTypes.object,
};

export default PatientForm;
